import { getLoginType, MAGIC_LINK } from '../resources/auth';
import { isEmpty } from 'lodash';
import { FINLAND, GREAT_BRITAIN, NORWAY, SWEDEN } from '../constants/CountryCodes';
import { ENGLISH, FINNISH, NORWEGIAN, SWEDISH } from '../constants/LanguageCodes';
import dayjs from 'dayjs';
import localeFi from 'antd/es/date-picker/locale/fi_FI';
import localeSe from 'antd/es/date-picker/locale/sv_SE';
import localeNo from 'antd/es/date-picker/locale/nb_NO';
import localeEn from 'antd/es/date-picker/locale/en_GB';

export function isMobileDevice() {
  return (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
}

export function showLogoutButton() {
  return !isMobileDevice() && getLoginType() !== MAGIC_LINK;
}

export function formatDateTime(dateTimeString, i18n, countryCode) {
  if (isEmpty(dateTimeString)) return '-';
  return createDayjsObject(dateTimeString, countryCode).format(getDateFormat(i18n)+' '+getTimeFormat(i18n));
}

export function getDateFormat(i18n) {
  if (i18n.language === 'fi') {
    return 'DD.MM.YYYY';
  } else {
    return 'YYYY-MM-DD';
  }
}

export function getTimeFormat(i18n) {
  switch (i18n.language) {
  case 'fi': {
    return 'HH.mm';
  }
  default: {
    return 'HH:mm';
  }
  }
}


export function getLocale(i18n) {
  switch (i18n.language) {
  case 'fi': {
    return localeFi;
  }
  case 'sv': {
    return localeSe;
  }
  case 'no': {
    return localeNo;
  }
  default: {
    return localeEn;
  }
  }
}

export function getPrivacyPolicyWebsiteUrl(countryCode, t) {
  switch (countryCode) {
  case FINLAND:
    return t('privacyPolicy.finlandWebsite');
  case SWEDEN:
    return t('privacyPolicy.swedenWebsite');
  case NORWAY:
    return t('privacyPolicy.norwayWebsite');
  }
}


export function generateFormattedHTML(html, classSelector) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const elements = doc.getElementsByClassName(classSelector);

  if (elements.length > 0) {
    const modifiedHtml = Array.from(elements)
      .map(element => {
        const links = element.getElementsByTagName('a');
        Array.from(links).forEach(link => {
          const span = document.createElement('span');
          span.innerHTML = link.innerHTML;
          link.parentNode.replaceChild(span, link);
        });
        return element.innerHTML.trim();
      })
      .join('');
    return modifiedHtml;
  }

  return null;
}

export function isPageReloadOrRefresh() {
  const entries = performance.getEntriesByType('navigation');
  return entries.some(entry => entry.type === 'reload' || entry.type === 'navigate');
}

export const mapToLanguageCode = countryCode => {
  switch (countryCode) {
  case GREAT_BRITAIN:
    return ENGLISH;
  case FINLAND:
    return FINNISH;
  case NORWAY:
    return NORWEGIAN;
  case SWEDEN:
    return SWEDISH;
  default:
    return ENGLISH;
  }
};

/**
 * Get timezone from country code, or browser's timezone as a default
 * @param {string} countryCode Country code (FI, NO, SE)
 * @return {string} Timezone string, eg. Europe/Helsinki
 */
export const getTimezoneFromCountry = countryCode => {
  switch (countryCode) {
  case 'FI':
    return 'Europe/Helsinki';
  case 'NO':
    return 'Europe/Oslo';
  case 'SE':
    return 'Europe/Stockholm';
  default:
    // eslint-disable-next-line new-cap
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
};

/**
 * Convert given dayjs, or an ISO string to a dayjs with timezone.
 * Timezone is decided from the country code, or it uses browser's timezone
 * @param {dayjs.Dayjs|String|undefined} value Dayjs object, ISO string, or undefined
 * @param {string|undefined} countryCode Country code to set timezone from
 * @return {dayjs.Dayjs|undefined} Dayjs object with timezone, or undefined
 */
export const createDayjsObject = (value, countryCode = undefined) => {
  if (isEmpty(value)) {
    return undefined;
  }
  if (dayjs.isDayjs(value)) {
    return value.tz(getTimezoneFromCountry(countryCode), true);
  }
  return dayjs.utc(value).tz(getTimezoneFromCountry(countryCode));
};

/**
 * Convert given dayjs, or an ISO string to a dayjs with timezone.
 * Timezone is decided from the country code, or it uses browser's timezone
 * @param {string|undefined} countryCode Country code to set timezone from
 * @return {dayjs.Dayjs|undefined} Dayjs object with timezone, or undefined
 */
export const createNewDayjsObject = (countryCode = undefined) => {
  return dayjs.utc().tz(getTimezoneFromCountry(countryCode));
};

